<script setup lang="ts">
import catImage from "@images/pets/cat.png";
import dogImage from "@images/pets/dog.png";
import useActionLoaderStore from "@/stores/actionLoader.store";

const actionLoaderStore = useActionLoaderStore();
const { t } = useI18n();

const petImage = computed(() =>
  actionLoaderStore.pet === "dog" ? dogImage : catImage,
);

// `messageArgs` carries batch progress (e.g. `{ done, total }`) for long conversions; plain
// messages have none, and vue-i18n treats an empty named object as a no-op interpolation.
const loaderMessage = computed(() =>
  t(actionLoaderStore.messageKey, actionLoaderStore.messageArgs ?? {}),
);
</script>

<template>
  <Teleport to="body">
    <Transition name="action-loader-fade">
      <div
        v-if="actionLoaderStore.isVisible"
        class="action-loader"
      >
        <div class="action-loader__backdrop" />

        <div class="action-loader__card">
          <VAvatar
            size="88"
            rounded="lg"
            variant="tonal"
            class="mb-4"
          >
            <VImg :src="petImage" />
          </VAvatar>

          <div class="text-h5 mb-2">
            {{ t("alkokh.global.loader.title") }}
          </div>

          <div class="text-body-1 text-medium-emphasis mb-4">
            {{ loaderMessage }}
          </div>

          <VProgressCircular
            indeterminate
            color="primary"
            size="34"
            width="3"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.action-loader-fade-enter-active,
.action-loader-fade-leave-active {
  transition: opacity 0.2s ease;
}

.action-loader-fade-enter-from,
.action-loader-fade-leave-to {
  opacity: 0;
}

.action-loader {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.38);
    backdrop-filter: blur(4px);
  }

  &__card {
    position: relative;
    z-index: 1;
    inline-size: min(90vw, 360px);
    border-radius: 24px;
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 24px 60px rgba(var(--v-shadow-key-umbra-color, 15, 23, 42), 0.18);
    padding: 2rem 1.5rem;
    text-align: center;
  }
}
</style>
